import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { Container, Masonry } from 'components'
import GcrmDialog from 'components/dialog/gcrm-dialog'
import { useDateTime, usePageTitle, useUser } from 'hooks'
import { generateVersionCode } from 'utils/common'

import { GcrmNotificationState, useExperiments } from '../../../hooks/use-experiment'
import { DeviceStatus, getDevicesApi, getPki } from '../devices/api'
import DeviceSummary from '../devices/device-summary'
import PlaceSummary from '../places/place-summary'
import PrivacySummary from '../privacy/privacy-summary'
import ProfileSummary from '../profile/profile-summary'
import SecuritySummary from '../security/security-summary'
import SCloudSummary from '../services/scloud/scloud-summary'
import ServiceSummary from '../services/service-summary'

export type Device = {
  name: string | null
  deviceName: string
  deviceDisplayName: string
  time: string
  type: string
  modelCode: string
  modelName: string
  imageUrl: string
  status?: string
}

export type SigninDeviceProps = {
  signinDevices: Device[]
  isSigninDevicesLoading: boolean
}

export function Home() {
  const [signinDevices, setSigninDevices] = useState([] as Device[])
  const [isSigninDevicesLoading, setIsSigninDevicesLoading] = useState(true)
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [gcrmDialogState, setGcrmDialogState] = useState<GcrmNotificationState | null>(null)
  const [cookies, setCookie] = useCookies()
  const { user } = useUser(true)

  usePageTitle()

  const dateTime = useDateTime()
  const { getGcrmNotificationStates } = useExperiments()

  useEffect(() => {
    if (!user) {
      return
    }

    const init = async () => {
      const gcrmDialogStates: GcrmNotificationState[] = await getGcrmNotificationStates(user.countryCode)
      if (gcrmDialogStates.length === 0) {
        return
      }

      const userAccounts = user.accounts.map((account) => account.value)

      const selectedGCRMDialogState = gcrmDialogStates.find((state) => {
        const cookieKey = 'sa_deny_gcrm_notification_' + generateVersionCode(state.version.toString())
        return !isDialogCheckedByAccount(userAccounts, cookieKey)
      })

      if (selectedGCRMDialogState) {
        setGcrmDialogState(selectedGCRMDialogState)
        setOpenDialog(true)
      }
    }

    init()
  }, [user.countryCode])

  useEffect(() => {
    setIsSigninDevicesLoading(true)
    getDevicesApi()
      .then((deviceResponses) => {
        setSigninDevices(
          deviceResponses
            .filter((d) => d.status === DeviceStatus.ACTIVATED)
            .map((device) => {
              return {
                deviceName:
                  device.deviceDisplayName || getMarketingName(device.modelCode) || device.modelName || device.modelCode,
                modelCode: device.modelCode,
                modelName: device.modelName,
                time: device.signedIn.dateTime && dateTime(device.signedIn.dateTime),
                type: device.type,
                imageUrl: device.imageUrl
              } as Device
            })
        )
      })
      .catch(() => {
        return [] as Device[]
      })
      .finally(() => {
        setIsSigninDevicesLoading(false)
      })
  }, [])

  function getMarketingName(modelCode: string) {
    getPki(modelCode)
      .then((productResource) => {
        return productResource.marketingName
      })
      .catch(() => {
        return undefined
      })
  }

  const isDialogCheckedByAccount = (accounts: string[], cookieKey: string) => {
    const savedAccounts = getGCRMCookieValues(cookieKey)

    return accounts.some((account) => savedAccounts.includes(account))
  }

  const updateGCRMCookie = () => {
    const cookieKey = 'sa_deny_gcrm_notification_' + generateVersionCode(gcrmDialogState?.version?.toString() ?? '')
    const savedAccounts = getGCRMCookieValues(cookieKey)

    const accounts = user.accounts.map((account) => account.value)
    const updatedAccounts = [...savedAccounts, ...accounts]
    const encoded = btoa(updatedAccounts.join('|'))

    setCookie(cookieKey, encoded, {
      domain: process.env.REACT_APP_COOKIE_DOMAIN,
      path: '/',
      expires: new Date(gcrmDialogState?.end ?? Date.now() + 60000)
    })
  }

  const getGCRMCookieValues = (cookieKey: string) => {
    const cookieValue = cookies[cookieKey] || ''

    let savedAccounts: string[] = []
    try {
      const decodedCookie = atob(cookieValue)
      savedAccounts = decodedCookie.split('|').filter(Boolean)
    } catch (error) {}

    return savedAccounts
  }

  const handleOk = () => {
    updateGCRMCookie()
    setOpenDialog(false)
  }

  return (
    <Container sx={{ marginTop: { xs: '0px', lg: '16px' } }}>
      <Masonry columns={{ xs: 1, md: 2, lg: 3 }} spacing={2} sx={{ margin: '0px' }}>
        <ProfileSummary />
        <SecuritySummary signinDevices={signinDevices} isSigninDevicesLoading={isSigninDevicesLoading} />
        <PrivacySummary />
        <PlaceSummary />
        <ServiceSummary />
        <DeviceSummary signinDevices={signinDevices} isSigninDevicesLoading={isSigninDevicesLoading} />
        <SCloudSummary />
      </Masonry>
      <GcrmDialog openDialog={openDialog} handleOk={handleOk} dialogState={gcrmDialogState} />
    </Container>
  )
}
